'use client';
import React, { useState, useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const faqs = [
  {
    question: 'Bagaimana sistem pembayaran untuk layanan IoT di Agro Teknologi Nusantara?',
    answer: 'Layanan IoT kami menggunakan sistem berlangganan berbasis titik perangkat (per node). Tarif dikenakan per bulan per titik dan dapat disesuaikan dengan kebutuhan. Untuk estimasi harga dan skema penggunaan, silakan hubungi tim kami untuk sesi konsultasi gratis.',
  },
  {
    question: 'Apakah Agro Teknologi Nusantara menyediakan pelatihan pertanian berbasis teknologi?',
    answer: 'Ya, kami menyediakan dua metode pelatihan: (1) Online: Peserta menerima perangkat dan bahan pelatihan terlebih dahulu sesuai tema yang dipilih, pelatihan dilakukan secara daring dan dipandu mentor. (2) Offline: Dilakukan di laboratorium pelatihan Agro Teknologi Nusantara dengan perangkat komputer saling terkoneksi, hands-on dan interaktif. Untuk kebutuhan event, bekerja sama dengan EO lokal.',
  },
  {
    question: 'Apa saja perangkat IoT yang tersedia di Agro Teknologi Nusantara?',
    answer: 'Sensor suhu, kelembaban tanah, curah hujan, pH tanah, serta sistem kontrol irigasi otomatis. Semua perangkat dapat terintegrasi dengan dashboard monitoring.',
  },
  {
    question: 'Apakah data pertanian saya akan digunakan oleh Agro Teknologi Nusantara?',
    answer: 'Ya, setiap perangkat IoT terintegrasi memberikan izin untuk mengonsumsi dan menganalisis data secara anonim. Data digunakan untuk: pengembangan sistem (forecasting, proyeksi hasil), penelitian dan inovasi, kolaborasi komunitas pengembang.',
  },
  {
    question: 'Siapa saja yang bisa bergabung dengan komunitas Agro Teknologi Nusantara?',
    answer: 'Terbuka untuk: petani digital, pengembang teknologi (hardware/software), pelajar & mahasiswa, startup agritech, dan siapa saja yang tertarik teknologi pertanian. Mendukung berbagi pengetahuan, riset kolaboratif, pengembangan proyek, dan akses data nyata dari lapangan.',
  },
  {
    question: 'Bagaimana jika saya ingin memulai dari nol dan belum paham teknologinya?',
    answer: 'Tenang, tidak harus ahli teknologi. Agro Teknologi Nusantara memiliki kurikulum pelatihan step-by-step, baik untuk petani maupun pemula. Dukungan teknis dan mentor siap membimbing dari awal.',
  },
  {
    question: 'Apakah saya bisa mencoba layanan Agro Teknologi Nusantara sebelum berlangganan?',
    answer: 'Tentu. Dapat berkonsultasi langsung untuk demo penggunaan IoT di lokasi atau melalui sesi online. Juga tersedia program uji coba terbatas (pilot project) untuk wilayah atau mitra tertentu.',
  },
  {
    question: 'Apakah Agro Teknologi Nusantara menyediakan alat dan bahan pertanian?',
    answer: 'Ya: Paket/Kit Irigasi (selang irigasi, pompa, tandon air), Perangkat IoT Kit (untuk pembelajaran), dan alat pertanian lainnya.',
  },
  {
    question: 'Apakah Agro Teknologi Nusantara menyediakan paket lengkap untuk sistem irigasi otomatis?',
    answer: 'Ya, termasuk selang irigasi, pompa air, tandon air, dan perangkat kontrol otomatis. Semua komponen dapat diintegrasikan dengan sistem monitoring IoT untuk kontrol jarak jauh.',
  },
];

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <main className="bg-canopy min-h-screen" ref={sectionRef}>
      <Header />

      <div className="pt-32 pb-20 px-6 md:px-10 blueprint-grid">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-px bg-chartreuse opacity-60" />
              <span className="annotation">PERTANYAAN UMUM</span>
            </div>
            <h1 className="font-mono font-bold text-4xl md:text-5xl text-text-primary mb-4 leading-tight">
              Frequently Asked<br />
              <span className="text-chartreuse">Questions.</span>
            </h1>
            <p className="text-text-muted text-lg leading-relaxed">
              Pertanyaan yang sering diajukan tentang layanan dan produk Agro Teknologi Nusantara.
            </p>
          </div>

          {/* FAQ list */}
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`border rounded-sm overflow-hidden ${
                  openIndex === i
                    ? 'border-chartreuse/40 bg-pond/40'
                    : 'border-chartreuse/10 bg-canopy/30 hover:border-chartreuse/20'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full text-left px-6 py-5 flex items-start gap-4"
                >
                  <span className="font-mono font-black text-chartreuse text-xs mt-0.5 shrink-0 w-6">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-mono font-bold text-text-primary text-sm leading-snug">
                      {faq.question}
                    </h3>
                  </div>
                  <span className={`font-mono text-chartreuse text-lg shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-45' : ''}`}>
                    +
                  </span>
                </button>

                {openIndex === i ? (
                  <div className="faq-answer-open px-6 pb-6 pl-16">
                    <div className="border-t border-chartreuse/10 pt-4">
                      <p className="text-text-muted text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="faq-answer-closed px-6 pb-6 pl-16" />
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="reveal mt-16 border border-chartreuse/20 rounded-sm p-8 bg-pond/30 text-center">
            <p className="text-text-muted text-base mb-4">
              Tetap ada pertanyaan yang belum terjawab?
            </p>
            <a
              href="https://wa.me/6281253837777?text=Halo%20Agro Teknologi Nusantara,%20saya%20punya%20pertanyaan%20tentang%20layanan"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-chartreuse px-8 py-4 rounded-sm text-sm uppercase tracking-widest inline-block"
            >
              Hubungi Kami →
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
